/**
 * ShipmentTable Component
 * Displays paginated, sortable list of shipments with actions
 * Responsive design: table on desktop, cards on mobile
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Text,
  Flex,
  HStack,
  VStack,
  Skeleton,
  useToast,
  IconButton,
  useBreakpointValue,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaShip, FaFileAlt, FaInfoCircle, FaFilePdf, FaEnvelope, FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getShipments, getShipmentDetail, getShipmentContainers, getBOLDraft, computeStatusBucket } from '../services/shipment.service';
import { getStoredUser } from '../services/auth.service';
import { PaginationParams, ShipmentListResponse, ShipmentListItem } from '../types/shipment.types';
import ShipmentDetailsModal from './ShipmentDetailsModal';
import { generateBillOfLadingPDF } from '../services/pdf.service';
import { sendBLDraftEmail } from '../services/email.service';

interface ShipmentTableProps {
  paginationParams: PaginationParams;
  onPageChange: (page: number) => void;
}

const ShipmentTable: React.FC<ShipmentTableProps> = ({ paginationParams, onPageChange }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useLanguage();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<ShipmentListResponse | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentListItem | null>(null);
  const [dueNumber, setDueNumber] = useState('');
  const [savingDue, setSavingDue] = useState(false);
  const [detailsShipmentId, setDetailsShipmentId] = useState<string | number | null>(null);
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();

  /**
   * Load shipments whenever params change
   */
  useEffect(() => {
    const loadShipments = async () => {
      setLoading(true);
      try {
        const data = await getShipments(paginationParams);
        setResponse(data);
      } catch (error) {
        console.error('Error loading shipments:', error);
        toast({
          title: 'Error',
          description: 'Failed to load shipments',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadShipments();
  }, [paginationParams, toast]);

  /**
   * Get status badge color
   */
  const getStatusColor = (status: string): string => {
    if (!status) return 'gray';
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    
    // Check for 'Averbada' specifically
    if (normalizedStatus === 'averbada') {
      return 'green';
    }

    switch (normalizedStatus) {
      case 'draft':
        return 'gray';
      case 'in_progress':
        return 'blue';
      case 'submitted':
        return 'yellow';
      case 'finalizada':
      case 'completed':
      case 'finished':
        return 'green';
      case 'delayed':
        return 'orange';
      default:
        return 'gray';
    }
  };

  /**
   * Format status text
   */
  const formatStatus = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Handle Draft button click - open modal for DUE number
   */
  const handleSubmitSI = (shipment: ShipmentListItem) => {
    setSelectedShipment(shipment);
    setDueNumber(''); // Reset the input
    onOpen();
  };

  /**
   * Save DUE number and proceed to draft BL page
   */
  const handleSaveDueNumber = async () => {
    if (!selectedShipment) return;

    if (!dueNumber.trim()) {
      toast({
        title: t('table.due_required'),
        description: t('table.due_required_desc'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSavingDue(true);

    try {
      const { supabase } = await import('../config/supabase');

      // Save the DUE number to the customs_due table.
      // The DUE record is linked by shipment_id, not by shipment_number.
      const { data: updateData, error: updateError } = await supabase
        .from('customs_due')
        .update({ due: dueNumber })
        .eq('shipment_id', selectedShipment.id);

      let error = updateError;
      const updateRows = Array.isArray(updateData) ? updateData : [];
      const updatedRows = updateRows.length;

      if (!error && updatedRows === 0) {
        const { error: insertError } = await supabase
          .from('customs_due')
          .insert({ shipment_id: selectedShipment.id, due: dueNumber });
        error = insertError;
      }

      if (error) {
        // Log the error but do NOT block navigation — the user must still be able
        // to proceed to the draft form even if the DUE save fails.
        console.error('Error saving DUE number to customs_due:', error);
        toast({
          title: t('table.due_save_failed'),
          description: error.message,
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('table.due_saved_title'),
          description: t('table.due_saved_desc'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Error saving DUE number:', error);
      toast({
        title: t('table.due_save_failed'),
        description: error.message || t('table.due_save_failed'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingDue(false);
      onClose();
      // Always navigate — the draft form is the primary goal;
      // the DUE save is a secondary record-keeping action.
      navigate(`/bol/draft/${selectedShipment.id}`);
    }
  };

  /**
   * Check deadline: Combine deadline_draft date with deadline_draft_time
   */
  const isDeadlineInFuture = (shipment: ShipmentListItem): boolean => {
    if (!shipment.deadline_draft) {
      return false;
    }

    // Create deadline date/time — append T00:00:00 so date-only strings parse in local time, not UTC
    const deadlineDate = new Date(shipment.deadline_draft.includes('T') ? shipment.deadline_draft : `${shipment.deadline_draft}T00:00:00`);
    
    // If deadline_draft_time exists, parse and add it to the date
    if (shipment.deadline_draft_time) {
      const timeParts = shipment.deadline_draft_time.match(/(\d+):(\d+)/);
      if (timeParts) {
        deadlineDate.setHours(parseInt(timeParts[1], 10), parseInt(timeParts[2], 10), 0, 0);
      }
    } else {
      // If no time specified, set to end of day
      deadlineDate.setHours(23, 59, 59, 999);
    }

    const now = new Date();
    return deadlineDate >= now;
  };

  /**
   * Check if DRAFT button should be shown
   * Show if: draft_submitted = false AND deadline >= today/time
   */
  const shouldShowSubmitSI = (shipment: ShipmentListItem): boolean => {
    const submitted = String(shipment.draft_submitted).toLowerCase();

    // Must NOT be submitted (false or null)
    if (submitted === 'true') {
      return false;
    }

    // Check if deadline is in the future
    return isDeadlineInFuture(shipment);
  };

  /**
   * Check if Print BL button should be shown
   * Show if: draft_submitted = true AND deadline >= today/time
   */
  const shouldShowPrintBL = (shipment: ShipmentListItem): boolean => {
    const submitted = String(shipment.draft_submitted).toLowerCase();
    
    // Must be submitted
    if (submitted !== 'true') {
      return false;
    }
    
    // Check if deadline is in the future
    return isDeadlineInFuture(shipment);
  };

  /**
   * Handle view shipment details
   */
  const handleViewDetails = (shipmentId: string | number) => {
    setDetailsShipmentId(shipmentId);
    onDetailsOpen();
  };

  /**
   * Handle Print BL click - Generate PDF
   */
  const handlePrintBL = async (shipment: ShipmentListItem) => {
    try {
      toast({
        title: 'Generating PDF',
        description: 'Please wait while we generate your Bill of Lading...',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      const [shipmentDetails, containers, bolDraft] = await Promise.all([
        getShipmentDetail(String(shipment.id)),
        getShipmentContainers(String(shipment.id)),
        getBOLDraft(String(shipment.id)),
      ]);

      // BOL draft data takes priority over raw shipment fields
      const d = (bolDraft?.draft_data as any) || {};
      const mergedData = {
        ...shipmentDetails,
        shipper_name:          d.shipper_name          || shipmentDetails.shipper_name,
        shipper_address:       d.shipper_address        || shipmentDetails.shipper_address,
        consignee_name:        d.consignee_name         || shipmentDetails.consignee_name,
        consignee_address:     d.consignee_address      || shipmentDetails.consignee_address,
        notify_party_name:     d.notify_party_name      || shipmentDetails.notify_party_name,
        notify_party_address:  d.notify_party_address   || shipmentDetails.notify_party_address,
        delivery_instructions: d.delivery_instructions  || shipmentDetails.delivery_instructions,
        pre_carriage_by:       d.pre_carriage_by        || shipmentDetails.pre_carriage_by,
        place_of_receipt:      d.place_of_receipt       || shipmentDetails.place_of_receipt,
        vessel:                d.vessel                 || shipmentDetails.vessel,
        voyage:                d.voyage                 || shipmentDetails.voyage,
        port_of_loading:       d.port_of_loading        || shipmentDetails.port_of_loading,
        port_of_discharge:     d.port_of_discharge      || shipmentDetails.port_of_discharge,
        place_of_delivery:     d.place_of_delivery      || shipmentDetails.place_of_delivery,
        marks_and_numbers:     d.marks_and_numbers      || shipmentDetails.marks_and_numbers,
        ncm_hs:                d.ncm_hs                 || shipmentDetails.ncm_hs,
        package_description:   d.package_description    || shipmentDetails.package_description,
        gross_weight:          d.gross_weight            || shipmentDetails.gross_weight,
        measurement:           d.measurement            || shipmentDetails.measurement,
        freight_details:       d.freight_details        || shipmentDetails.freight_details,
        freight_payable_at:    d.freight_payable_at     || shipmentDetails.freight_payable_at,
        prepaid:               d.prepaid                ?? shipmentDetails.prepaid,
        collect:               d.collect                ?? shipmentDetails.collect,
        place_date_of_issue:   d.place_date_of_issue   || shipmentDetails.place_date_of_issue,
        number_of_original_bl: d.number_of_original_bl  || shipmentDetails.number_of_original_bl,
        bl_signature:          d.bl_signature           || shipmentDetails.bl_signature,
      };

      await generateBillOfLadingPDF(mergedData, containers);

      toast({
        title: 'Success',
        description: 'Bill of Lading PDF generated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSendEmail = async (shipment: ShipmentListItem) => {
    try {
      const user = getStoredUser();
      if (!user?.email) throw new Error('User email not found');

      toast({ title: 'Preparing email…', description: 'Generating PDF and sending…', status: 'info', duration: 2000, isClosable: true });

      const [shipmentDetails, containers, bolDraft] = await Promise.all([
        getShipmentDetail(String(shipment.id)),
        getShipmentContainers(String(shipment.id)),
        getBOLDraft(String(shipment.id)),
      ]);

      const d = (bolDraft?.draft_data as any) || {};
      const mergedData = {
        ...shipmentDetails,
        shipper_name:          d.shipper_name          || shipmentDetails.shipper_name,
        consignee_name:        d.consignee_name         || shipmentDetails.consignee_name,
        vessel:                d.vessel                 || shipmentDetails.vessel,
        voyage:                d.voyage                 || shipmentDetails.voyage,
        port_of_loading:       d.port_of_loading        || shipmentDetails.port_of_loading,
        port_of_discharge:     d.port_of_discharge      || shipmentDetails.port_of_discharge,
        marks_and_numbers:     d.marks_and_numbers      || shipmentDetails.marks_and_numbers,
        ncm_hs:                d.ncm_hs                 || shipmentDetails.ncm_hs,
        package_description:   d.package_description    || shipmentDetails.package_description,
        gross_weight:          d.gross_weight            || shipmentDetails.gross_weight,
        measurement:           d.measurement            || shipmentDetails.measurement,
        freight_details:       d.freight_details        || shipmentDetails.freight_details,
        freight_payable_at:    d.freight_payable_at     || shipmentDetails.freight_payable_at,
        prepaid:               d.prepaid                ?? shipmentDetails.prepaid,
        collect:               d.collect                ?? shipmentDetails.collect,
        place_date_of_issue:   d.place_date_of_issue   || shipmentDetails.place_date_of_issue,
        number_of_original_bl: d.number_of_original_bl  || shipmentDetails.number_of_original_bl,
        bl_signature:          d.bl_signature           || shipmentDetails.bl_signature,
        shipper_address:       d.shipper_address        || shipmentDetails.shipper_address,
        consignee_address:     d.consignee_address      || shipmentDetails.consignee_address,
        notify_party_name:     d.notify_party_name      || shipmentDetails.notify_party_name,
        notify_party_address:  d.notify_party_address   || shipmentDetails.notify_party_address,
        delivery_instructions: d.delivery_instructions  || shipmentDetails.delivery_instructions,
        pre_carriage_by:       d.pre_carriage_by        || shipmentDetails.pre_carriage_by,
        place_of_receipt:      d.place_of_receipt       || shipmentDetails.place_of_receipt,
        place_of_delivery:     d.place_of_delivery      || shipmentDetails.place_of_delivery,
      };

      const pdfBase64 = await generateBillOfLadingPDF(mergedData, containers, { returnBase64: true });
      if (!pdfBase64) throw new Error('Failed to generate PDF');

      const result = await sendBLDraftEmail({
        toEmail: user.email,
        shipmentRef: shipmentDetails.brl_reference || shipmentDetails.shipment_number,
        vessel: mergedData.vessel,
        portOfLoading: mergedData.port_of_loading,
        portOfDischarge: mergedData.port_of_discharge,
        pdfBase64: pdfBase64 as string,
      });

      if (!result.success) throw new Error(result.error);

      toast({ title: 'Email sent', description: `Draft BL sent to ${user.email}`, status: 'success', duration: 4000, isClosable: true });
    } catch (err: any) {
      toast({ title: 'Failed to send email', description: err.message || 'Unknown error', status: 'error', duration: 5000, isClosable: true });
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Format deadline with time (date + hh:mm)
   */
  const formatDeadline = (dateString?: string, timeString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    
    if (timeString) {
      return `${dateStr} ${timeString}`;
    }
    
    return dateStr;
  };

  if (loading) {
    return (
      <VStack spacing={3} align="stretch">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height="44px" borderRadius="sm" startColor="appleSurface" endColor="appleCardBorder" />
        ))}
      </VStack>
    );
  }

  if (!response || !response.success) {
    return (
      <Box bg="appleElevated" borderWidth="1px" borderColor="appleCardBorder" p={8} borderRadius="md" boxShadow="sm" textAlign="center">
        <Text color="red.500" fontSize="lg" fontWeight="medium">
          {response?.error || 'Failed to load shipments'}
        </Text>
      </Box>
    );
  }

  if (response.shipments.length === 0) {
    return (
      <Box bg="appleElevated" borderWidth="1px" borderColor="appleCardBorder" p={12} borderRadius="md" boxShadow="sm" textAlign="center">
        <FaShip size={64} color="gray" style={{ margin: '0 auto 16px' }} />
        <Heading size="md" mb={2} color="appleTextPrimary">
          No Shipments Found
        </Heading>
        <Text color="appleTextSecondary">
          {paginationParams.search || paginationParams.status_bucket
            ? 'Try adjusting your filters or search terms.'
            : 'No shipments assigned to your broker at this time. Check back soon or contact support.'}
        </Text>
      </Box>
    );
  }

  const { shipments, pagination } = response;

  /**
   * Render mobile card view
   */
  if (isMobile) {
    return (
      <VStack spacing={4} align="stretch">
        {shipments.map(shipment => (
          <Card key={shipment.id} variant="outline">
            <CardHeader pb={2}>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Heading size="sm">{shipment.shipment_number}</Heading>
                  <Text fontSize="xs" color="appleTextSecondary">
                    {shipment.booking || 'No booking'}
                  </Text>
                </VStack>
                <VStack align="end" spacing={0}>
                  <Badge colorScheme={getStatusColor(shipment.due_status)}>
                    {shipment.due_status || 'N/A'}
                  </Badge>
                  <Text fontSize="10px" color="appleTextSecondary">
                    {formatStatus(shipment.status)}
                  </Text>
                </VStack>
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              <VStack align="stretch" spacing={2}>
                <Box>
                  <Text fontSize="xs" color="appleTextSecondary">
                    Shipper
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {shipment.shipper_name || 'N/A'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="appleTextSecondary">
                    Order
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {shipment.shipper_order || 'N/A'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="appleTextSecondary">
                    Vessel
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {shipment.vessel || 'N/A'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="appleTextSecondary">
                    Draft Deadline
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatDeadline(shipment.deadline_draft, shipment.deadline_draft_time)}
                  </Text>
                </Box>
                <Divider />
                <HStack spacing={2}>
                  <IconButton
                    aria-label="View shipment details"
                    icon={<FaInfoCircle />}
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => handleViewDetails(shipment.id)}
                    title="View details"
                  />
                  {shouldShowSubmitSI(shipment) && (
                    <Button
                      colorScheme="blue"
                      size="sm"
                      leftIcon={<FaFileAlt />}
                      onClick={() => handleSubmitSI(shipment)}
                      flex={1}
                    >
                      Draft
                    </Button>
                  )}
                  {shouldShowPrintBL(shipment) && (
                    <>
                    <Button
                      colorScheme="green"
                      size="sm"
                      leftIcon={<FaFilePdf />}
                      onClick={() => handlePrintBL(shipment)}
                      flex={1}
                    >
                      Print BL
                    </Button>
                    <Button
                      colorScheme="purple"
                      size="sm"
                      leftIcon={<FaEnvelope />}
                      onClick={() => handleSendEmail(shipment)}
                      flex={1}
                      variant="outline"
                    >
                      Send Email
                    </Button>
                    </>
                  )}
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        ))}

        {/* Mobile Pagination */}
        <PaginationControls pagination={pagination} onPageChange={onPageChange} />
      </VStack>
    );
  }

  /**
   * Render desktop table view
   */
  return (
    <>
      {/* DUE Number Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(3px)" />
        <ModalContent borderRadius="lg" bg="appleElevated" borderWidth="1px" borderColor="appleCardBorder">
          <ModalHeader fontSize="lg" fontWeight="700" color="appleTextPrimary" borderBottom="1px solid" borderColor="appleCardBorder">
            Enter DUE Number
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">DUE Number</FormLabel>
              <Input
                variant="flushed"
                borderColor="appleCardBorder"
                focusBorderColor="appleAccent"
                placeholder="Enter DUE number"
                value={dueNumber}
                onChange={(e) => setDueNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveDueNumber();
                  }
                }}
                autoFocus
              />
            </FormControl>
            {selectedShipment && (
              <Box mt={5} p={3} bg="appleSurface" borderRadius="sm" borderWidth="1px" borderColor="appleCardBorder">
                <Text fontSize="10px" fontWeight="600" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider">Shipment</Text>
                <Text fontSize="sm" fontWeight="500" color="appleTextPrimary">{selectedShipment.shipment_number}</Text>
                <Text fontSize="10px" fontWeight="600" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider" mt={2}>Shipper</Text>
                <Text fontSize="sm" fontWeight="500" color="appleTextPrimary">{selectedShipment.shipper_name}</Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="appleCardBorder" pt={3}>
            <Button variant="ghost" mr={3} onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveDueNumber}
              isLoading={savingDue}
              leftIcon={<FaFileAlt />}
              size="sm"
            >
              Continue to Draft
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box overflowX="auto">
        <Table
          variant="simple"
          size="sm"
          sx={{
            'th, td': { borderColor: 'appleCardBorder' },
            'tbody tr:last-of-type td': { borderBottom: 'none' },
          }}
        >
          <Thead>
            <Tr>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Shipment</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Shipper</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Order</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Booking</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Vessel</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Draft</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Due Status</Th>
              <Th color="appleTextSecondary" borderColor="appleCardBorder">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {shipments.map(shipment => (
              <Tr
                key={shipment.id}
                _hover={{ bg: 'appleSurface' }}
                cursor="pointer"
                onClick={() => handleViewDetails(shipment.id)}
              >
                <Td fontWeight="medium" fontSize="xs" py={3}>{shipment.shipment_number}</Td>
                <Td fontSize="xs" py={3}>{shipment.shipper_name || 'N/A'}</Td>
                <Td fontSize="xs" py={3}>{shipment.shipper_order || 'N/A'}</Td>
                <Td fontSize="xs" py={3}>{shipment.booking || 'N/A'}</Td>
                <Td fontSize="xs" py={3}>{shipment.vessel || 'N/A'}</Td>
                <Td fontSize="xs" py={3}>{formatDeadline(shipment.deadline_draft, shipment.deadline_draft_time)}</Td>
                <Td py={3}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" fontWeight="medium" color="appleTextPrimary">
                      {shipment.due_number || 'N/A'}
                    </Text>
                    <Text fontSize="10px" color={`${getStatusColor(shipment.due_status)}.500`}>
                      {shipment.due_status || 'N/A'}
                    </Text>
                  </VStack>
                </Td>
                <Td py={3} onClick={(e) => e.stopPropagation()}>
                  <HStack spacing={1}>
                    {shouldShowSubmitSI(shipment) && (
                      <Button
                        size="xs"
                        colorScheme="blue"
                        leftIcon={<FaFileAlt />}
                        onClick={() => handleSubmitSI(shipment)}
                      >
                        Draft
                      </Button>
                    )}
                    {shouldShowPrintBL(shipment) && (
                      <>
                        <Button
                          size="xs"
                          colorScheme="green"
                          leftIcon={<FaFilePdf />}
                          onClick={() => handlePrintBL(shipment)}
                        >
                          Print BL
                        </Button>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            aria-label="More actions"
                            icon={<FaEllipsisV />}
                            size="xs"
                            variant="ghost"
                          />
                          <MenuList minW="160px">
                            <MenuItem icon={<FaEnvelope />} onClick={() => handleSendEmail(shipment)}>
                              Send Email
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Desktop Pagination */}
      <Box pt={4}>
        <PaginationControls pagination={pagination} onPageChange={onPageChange} />
      </Box>

      {/* Shipment Details Modal */}
      {detailsShipmentId && (
        <ShipmentDetailsModal
          isOpen={isDetailsOpen}
          onClose={() => {
            onDetailsClose();
            setDetailsShipmentId(null);
          }}
          shipmentId={detailsShipmentId}
        />
      )}
    </>
  );
};

/**
 * Pagination Controls Component
 */
interface PaginationControlsProps {
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  onPageChange: (page: number) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ pagination, onPageChange }) => {
  const { page, total, total_pages } = pagination;

  const startItem = (page - 1) * pagination.limit + 1;
  const endItem = Math.min(page * pagination.limit, total);

  return (
    <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
      <Text fontSize="xs" color="appleTextSecondary">
        Showing {startItem} to {endItem} of {total} shipments
      </Text>

      <HStack spacing={2}>
        <IconButton
          aria-label="Previous page"
          icon={<FaChevronLeft />}
          size="sm"
          onClick={() => onPageChange(page - 1)}
          isDisabled={page === 1}
          variant="outline"
        />

        {/* Page numbers */}
        {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
          let pageNum: number;
          if (total_pages <= 5) {
            pageNum = i + 1;
          } else if (page <= 3) {
            pageNum = i + 1;
          } else if (page >= total_pages - 2) {
            pageNum = total_pages - 4 + i;
          } else {
            pageNum = page - 2 + i;
          }

          return (
            <Button
              key={pageNum}
              size="sm"
              onClick={() => onPageChange(pageNum)}
              colorScheme={page === pageNum ? 'blue' : 'gray'}
              variant={page === pageNum ? 'solid' : 'outline'}
            >
              {pageNum}
            </Button>
          );
        })}

        <IconButton
          aria-label="Next page"
          icon={<FaChevronRight />}
          size="sm"
          onClick={() => onPageChange(page + 1)}
          isDisabled={page === total_pages}
          variant="outline"
        />
      </HStack>
    </Flex>
  );
};

export default ShipmentTable;
